import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Button } from '../layout/buttons';
import { BeepAnimate, BeepTalk } from '../animations/boopAnimate';
import { fetchCanvassOnboardingInterview } from '../../utils/canvasses';
import { sendDiscoveryChat } from '../../utils/discovery';
import styles from './CanvassAssistantChat.module.css';
import { getClient } from '../../utils/globalState';
import { config } from '../../config/environment';
import beepStatic from '../../assets/icons/augustsays/Beep-colour.svg';
import { logger } from '../../utils/logger';

const AnimatedText = ({ message, onComplete, styles }) => {
  const [wordIndex, setWordIndex] = useState(0);
  const tokens = useMemo(() => {
    const m = message || '';
    return m.match(/(\S+|\s+)/g) || [];
  }, [message]);
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    if (!tokens.length) return;

    if (wordIndex >= tokens.length) {
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true;
        if (onComplete) onComplete();
      }
      return;
    }

    const timer = setInterval(() => {
      setWordIndex((prev) => (prev < tokens.length ? prev + 1 : prev));
    }, 62);
    return () => clearInterval(timer);
  }, [wordIndex, tokens.length, onComplete]);

  const visibleCount = Math.min(wordIndex, tokens.length);

  return (
    <span className={styles.animatedTextWrap} style={{ whiteSpace: 'pre-wrap' }}>
      <span className={styles.animatedTextGhost} aria-hidden="true">
        {message || ''}
      </span>
      <span className={styles.animatedTextVisible}>
        {tokens.slice(0, visibleCount).map((t, i) => {
          const isWhitespace = /^\s+$/.test(t);
          if (isWhitespace) return <span key={i}>{t}</span>;
          return (
            <span key={i} className={styles.animatedToken}>
              {t}
            </span>
          );
        })}
      </span>
    </span>
  );
};

const SpeakingMessage = React.memo(({ content, styles, beepStatic, BeepTalk }) => {
  const [isAnimating, setIsAnimating] = useState(true);

  const handleComplete = () => {
    if (!isAnimating) return;
    setIsAnimating(false);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        flexWrap: 'nowrap',
      }}
    >
      <div style={{ flexShrink: 0 }}>
        {isAnimating ? (
          <BeepTalk />
        ) : (
          <img src={beepStatic} alt="Assistant" style={{ width: 55, height: 55 }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <AnimatedText
          message={typeof content === 'string' ? content : String(content)}
          onComplete={handleComplete}
          styles={styles}
        />
      </div>
    </div>
  );
});

/**
 * Bespoke, in-house chat for the Canvass assistant.
 * - Manages its own message list and input
 * - Calls the Copilot runtimeUrl directly and consumes responses
 * - Preserves existing extensions handling (handled by the global fetch override in CanvassInfo)
 */
const CanvassAssistantChat = forwardRef(({
  runtimeUrl,
  initialText,
  clientId,
  displayName,
  canvassInfo, // { canvassName, canvassDescription, ownerId }
  canvass, // full canvass object so we can self-seed from backend
  context, // { type, companyId, stakeholderId, contextData }
  interviewStarted,
  onInterviewStart,
  onPills,
  onConversationId,
  onExtractedInfo,
  onShouldTerminate,
  onTerminating,
}, ref) => {
  const [messages, setMessages] = useState([]); // {role: 'assistant'|'user', content: string}
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [threadId, setThreadId] = useState('');
  const [shouldTerminate, setShouldTerminate] = useState(false);
  const [terminateConvId, setTerminateConvId] = useState(null);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const listRef = useRef(null);
  const textareaRef = useRef(null);
  const ownerId = canvass?.ownerId || clientId || getClient()?.clientId;
  const clientDisplayName = displayName || canvass?.displayName || getClient()?.displayName;

  // If canvass is in progress, self-load prior interview messages and thread
  const loadedInterviewRef = useRef(false);
  useEffect(() => {
    const loadFromBackend = async () => {
      try {
        const canvassId = canvass?.canvassId || canvass?.id || null;
        if (!canvassId) return;
        if (canvass?.status !== 'in_progress') {
          loadedInterviewRef.current = false;
          return;
        }
        if (loadedInterviewRef.current) return;
        const result = await fetchCanvassOnboardingInterview({ canvassId, returnFull: true });
        const threads = result?.interviewMessage || result?.interviewMessages || [];
        const first = Array.isArray(threads) ? threads[0] : null;
        if (first?.messages && Array.isArray(first.messages)) {
          const normalized = first.messages.map(m => ({ role: m.role, content: m.content }));
          // Seed only if empty to avoid overwriting any local edits
          setMessages([...messages, ...normalized]);
        }
        const tId = first?.conversationId || first?.id || canvassId;
        if (tId && !threadId) setThreadId(String(tId));
        loadedInterviewRef.current = true;
      } catch (err) {
        logger.error('Failed to self-load onboarding interview messages:', err);
      }
    };
    loadFromBackend();
  }, [canvass?.canvassId, canvass?.id, canvass?.status]);

  useEffect(() => {
    if (initialText && messages.length === 0) {
      setMessages([{ role: 'intro', content: initialText }]);
    }
  }, [initialText, messages.length]);

  // Initialize threadId from initialThreadId; if absent, leave as null until backend returns conversationId
  useEffect(() => {
    try {
      // Use provided initialThreadId (e.g., external conversationId) if present; else leave null
      const id = (typeof canvass.id === 'string' && canvass.id.trim())
        ? canvass.id.trim()
        : null;
      setThreadId(id);
    } catch (err) {
      setThreadId(null);
    }
  }, [canvass]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  // When the latest message is from the assistant, mark it as the one being "spoken"
  useEffect(() => {
    if (!messages.length) return;
    const lastIndex = messages.length - 1;
    const last = messages[lastIndex];
    if (last.role === 'assistant') {
      setSpeakingIndex(lastIndex);
    }
  }, [messages]);

  const appendMessage = (role, content) => {
    if (!content) return;
    setMessages(prev => [...prev, { role, content }]);
  };

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 400)}px`;
  };

  const onTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Submit on Enter, allow Shift+Enter for newline
      e.preventDefault();
      onSend(e);
    }
  };

  const extractAssistantReply = (respJson) => {
    // Handle discovery service response format
    if (respJson?.question_for_user?.question) {
      return respJson.question_for_user.question;
    }
    
    // Handle direct question field (fallback)
    if (respJson?.question) {
      return respJson.question;
    }
    
    // Fallback to CopilotKit GraphQL shape (for backward compatibility)
    const g = respJson?.data?.generateCopilotResponse;
    if (g?.messages && Array.isArray(g.messages)) {
      // Find the last assistant message content (array of strings or a string)
      for (let i = g.messages.length - 1; i >= 0; i--) {
        const m = g.messages[i];
        if (m?.role === 'assistant') {
          if (Array.isArray(m.content)) return m.content.join('\n');
          if (typeof m.content === 'string') return m.content;
        }
      }
    }
    
    return null;
  };

  // Lifted/condensed extension processing based on logic in CanvassInfo.jsx
  const processExtensions = (data) => {
    try {
      // Handle discovery service response format (new primary path)
      if (data?.conversationId || data?.extracted_info || data?.question_for_user) {
        // Update conversation ID if provided
        if (data?.conversationId && onConversationId) {
          onConversationId(data.conversationId);
          if (data.conversationId && typeof data.conversationId === 'string') {
            setThreadId(data.conversationId);
          }
        }

        // Handle extracted info from discovery service
        if (data?.extracted_info && onExtractedInfo) {
          let extractedInfoArray = [];
          const extractedInfo = data.extracted_info;
          
          if (Array.isArray(extractedInfo)) {
            extractedInfoArray = extractedInfo.filter(item => {
              if (!item.value) return false;
              const lowercaseValue = String(item.value).toLowerCase();
              return !(
                lowercaseValue === 'unknown' ||
                lowercaseValue === 'unknown company' ||
                lowercaseValue === 'client' ||
                lowercaseValue === 'not specified' ||
                lowercaseValue === 'n/a' ||
                lowercaseValue === ''
              );
            });
          } else if (typeof extractedInfo === 'object' && extractedInfo !== null) {
            const obj = extractedInfo || {};
            const hasStructured = (
              obj.primary_objective || obj.objective ||
              Array.isArray(obj.sub_objectives) ||
              Array.isArray(obj.observed_behaviors) || typeof obj.observed_behaviors === 'string' ||
              Array.isArray(obj.external_context) || typeof obj.external_context === 'string' ||
              Array.isArray(obj.touchpoints) || typeof obj.touchpoints === 'string' ||
              typeof obj.audience === 'string'
            );

            if (hasStructured) {
              const pushIf = (key, value, tags) => {
                if (value && String(value).trim()) {
                  extractedInfoArray.push({ key, value: String(value).trim(), tags });
                }
              };
              const objective = obj.primary_objective || obj.objective;
              pushIf('objective', objective, ['research_objective']);
              if (typeof obj.audience === 'string') pushIf('audience', obj.audience, ['audience_profile']);
              const tp = Array.isArray(obj.touchpoints) ? obj.touchpoints : (typeof obj.touchpoints === 'string' ? [obj.touchpoints] : []);
              if (tp.length) pushIf('touchpoints', tp.join(', '), ['audience_profile']);
              const ob = Array.isArray(obj.observed_behaviors) ? obj.observed_behaviors : (typeof obj.observed_behaviors === 'string' ? [obj.observed_behaviors] : []);
              if (ob.length) pushIf('observed_behaviors', ob.join(', '), ['context']);
              const ec = Array.isArray(obj.external_context) ? obj.external_context : (typeof obj.external_context === 'string' ? [obj.external_context] : []);
              if (ec.length) pushIf('external_context', ec.join(', '), ['context']);
              const subs = Array.isArray(obj.sub_objectives) ? obj.sub_objectives : [];
              subs.forEach((s, i) => {
                const val = String(s || '').trim();
                if (val) extractedInfoArray.push({ key: `sub_${i}_title`, value: val, tags: ['sub_objective'] });
              });
            } else {
              for (const [key, value] of Object.entries(obj)) {
                if (value === null || value === undefined || value === '') continue;
                const lowercaseValue = String(value).toLowerCase();
                if ([
                  'unknown',
                  'unknown company',
                  'client',
                  'not specified',
                  'n/a'
                ].includes(lowercaseValue)) continue;
                let tag = 'general_info';
                if (['first_name', 'last_name', 'email', 'company', 'role', 'industry'].includes(key)) {
                  tag = 'client_profile';
                } else if (key.includes('strategic') || key.includes('objective')) {
                  tag = 'research_objective';
                } else if (key.includes('brand') || key.includes('tone')) {
                  tag = 'brand_context';
                } else if (key.includes('audience') || key.includes('demographic')) {
                  tag = 'audience_profile';
                } else if (key.includes('interest')) {
                  tag = 'points_of_interest';
                } else if (key.includes('pain') || key.includes('challenge')) {
                  tag = 'pain_point';
                }
                extractedInfoArray.push({ key, value: String(value), tags: [tag] });
              }
            }
          }

          if (extractedInfoArray.length > 0) {
            onExtractedInfo(extractedInfoArray);
          }
        }

        // Handle termination signal from discovery service
        if (data?.should_terminate === true) {
          const convId = data.conversationId;
          setShouldTerminate(true);
          setTerminateConvId(convId || null);
          onTerminating?.();
        }

        return; // Exit early if we processed discovery service format
      }

      // Handle CopilotKit GraphQL shape (for backward compatibility)
      const ext = data?.data?.generateCopilotResponse?.extensions;
      const generateCopilotResponse = data?.data?.generateCopilotResponse;
      if (!ext) return;

      if (onPills && 'pills' in ext) {
        onPills(ext.pills);
      }

      if (onConversationId && 'conversationId' in ext) {
        const conversationId = ext.conversationId;
        onConversationId(conversationId);
        // No localStorage writes; set local threadId to conversationId for subsequent requests
        if (conversationId && typeof conversationId === 'string') {
          setThreadId(conversationId);
        }
      } else if('threadId' in generateCopilotResponse) {
        const threadId = generateCopilotResponse.threadId;
        if (threadId && typeof threadId === 'string') {
          setThreadId(threadId);
        }
      }

      // Extract structured insights
      if (onExtractedInfo && ext.extractedInfo) {
        let extractedInfoArray = [];
        const extractedInfo = ext.extractedInfo;
        if (Array.isArray(extractedInfo)) {
          if (extractedInfo.length > 0 && extractedInfo[0].hasOwnProperty('key') && extractedInfo[0].hasOwnProperty('tags')) {
            extractedInfoArray = extractedInfo.filter(item => {
              if (!item.value) return false;
              const lowercaseValue = String(item.value).toLowerCase();
              return !(
                lowercaseValue === 'unknown' ||
                lowercaseValue === 'unknown company' ||
                lowercaseValue === 'client' ||
                lowercaseValue === 'not specified' ||
                lowercaseValue === 'n/a' ||
                lowercaseValue === ''
              );
            });
          }
        } else if (typeof extractedInfo === 'object' && extractedInfo !== null) {
          const obj = extractedInfo || {};
          const hasStructured = (
            obj.primary_objective || obj.objective ||
            Array.isArray(obj.sub_objectives) ||
            Array.isArray(obj.observed_behaviors) || typeof obj.observed_behaviors === 'string' ||
            Array.isArray(obj.external_context) || typeof obj.external_context === 'string' ||
            Array.isArray(obj.touchpoints) || typeof obj.touchpoints === 'string' ||
            typeof obj.audience === 'string'
          );

          if (hasStructured) {
            const pushIf = (key, value, tags) => {
              if (value && String(value).trim()) {
                extractedInfoArray.push({ key, value: String(value).trim(), tags });
              }
            };
            const objective = obj.primary_objective || obj.objective;
            pushIf('objective', objective, ['research_objective']);
            if (typeof obj.audience === 'string') pushIf('audience', obj.audience, ['audience_profile']);
            const tp = Array.isArray(obj.touchpoints) ? obj.touchpoints : (typeof obj.touchpoints === 'string' ? [obj.touchpoints] : []);
            if (tp.length) pushIf('touchpoints', tp.join(', '), ['audience_profile']);
            const ob = Array.isArray(obj.observed_behaviors) ? obj.observed_behaviors : (typeof obj.observed_behaviors === 'string' ? [obj.observed_behaviors] : []);
            if (ob.length) pushIf('observed_behaviors', ob.join(', '), ['context']);
            const ec = Array.isArray(obj.external_context) ? obj.external_context : (typeof obj.external_context === 'string' ? [obj.external_context] : []);
            if (ec.length) pushIf('external_context', ec.join(', '), ['context']);
            const subs = Array.isArray(obj.sub_objectives) ? obj.sub_objectives : [];
            subs.forEach((s, i) => {
              const val = String(s || '').trim();
              if (val) extractedInfoArray.push({ key: `sub_${i}_title`, value: val, tags: ['sub_objective'] });
            });
          } else {
            for (const [key, value] of Object.entries(obj)) {
              if (value === null || value === undefined || value === '') continue;
              const lowercaseValue = String(value).toLowerCase();
              if ([
                'unknown',
                'unknown company',
                'client',
                'not specified',
                'n/a'
              ].includes(lowercaseValue)) continue;
              let tag = 'general_info';
              if (['first_name', 'last_name', 'email', 'company', 'role', 'industry'].includes(key)) {
                tag = 'client_profile';
              } else if (key.includes('strategic') || key.includes('objective')) {
                tag = 'research_objective';
              } else if (key.includes('brand') || key.includes('tone')) {
                tag = 'brand_context';
              } else if (key.includes('audience') || key.includes('demographic')) {
                tag = 'audience_profile';
              } else if (key.includes('interest')) {
                tag = 'points_of_interest';
              } else if (key.includes('pain') || key.includes('challenge')) {
                tag = 'pain_point';
              }
              extractedInfoArray.push({ key, value: String(value), tags: [tag] });
            }
          }
        }

        if (extractedInfoArray.length > 0) {
          onExtractedInfo(extractedInfoArray);
        }
      }

      if (ext.should_terminate === true) {
        const convId = ext.conversationId;
        setShouldTerminate(true);
        setTerminateConvId(convId || null);
        onTerminating?.();
      }
    } catch {
      // ignore extension parsing errors
    }
  };

  const sendMessage = async (text, endInterview = false) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || isSending) return;

    // Call onInterviewStart if this is the first message and interview hasn't started yet
    if (!interviewStarted && onInterviewStart) {
      onInterviewStart();
    }

    // stop any current speaking animation when a new send starts
    setSpeakingIndex(null);
    appendMessage('user', trimmed);
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsSending(true);

    try {
      // Build request body for discovery service
      const canvassInfoPayload = {
        canvassName: canvassInfo?.canvassName || '',
        canvassBrief: canvassInfo?.canvassBrief || '',
        ownerId: ownerId || '',
        displayName: displayName || '',
        ...(canvassInfo || {}),
        ...(context && { context }),
      };

      const requestPayload = {
        user_message: trimmed,
        canvassInfo: canvassInfoPayload,
        messages: messages, // Send complete message history for stateless frontend pattern
        ...(endInterview && { endInterview: true }),
      };

      // Only include conversationId if threadId is present
      if (threadId) {
        requestPayload.conversationId = threadId;
      }

      const data = await sendDiscoveryChat(requestPayload);

      // Update threadId if returned by discovery service
      if (data?.conversationId && (!threadId || threadId !== data.conversationId)) {
        setThreadId(data.conversationId);
      }

      // Append assistant reply
      const assistant = extractAssistantReply(data);
      if (assistant) {
        appendMessage('assistant', assistant);
      }

      // Process extensions and bubble up to parent
      processExtensions(data);

      // Handle termination if needed
      if (data?.should_terminate) {
        setShouldTerminate(true);
        setTerminateConvId(data.conversationId);
        onTerminating?.();
      }
    } catch (err) {
      logger.error('Chat send failed:', err);
      appendMessage('assistant', 'Sorry, something went wrong. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const onSend = (e) => {
    e?.preventDefault?.();
    sendMessage(input.trim());
  };

  // Allow parent components to trigger the "Build the Canvass" message directly
  useImperativeHandle(ref, () => ({
    sendBuildMessage: () => sendMessage('Build the Canvass and end the interview now.', true),
  }));

  const LoadingIndicator = () => {
    try {
      if (BeepAnimate && typeof BeepAnimate === 'function') {
        return <BeepAnimate />;
      }
    } catch {}
    return (
      <div className={styles.typingDots} aria-hidden="true">
        <span></span><span></span><span></span>
      </div>
    );
  };

  return (
    <div className={styles.chatContainer}>
      <div ref={listRef} className={styles.messages}>
        {messages.map((m, idx) => {
          const content = (() => {
            if (typeof m?.content === 'string') return m.content;
            if (Array.isArray(m?.content)) return m.content.join('\n');
            if (m?.content && typeof m.content === 'object') {
              try { return JSON.stringify(m.content); } catch { return String(m.content); }
            }
            return m?.content != null ? String(m.content) : '';
          })();
          const isAssistant = m.role === 'assistant';
          const isSpeaking = !isSending && isAssistant && idx === speakingIndex;
          return (
            <div key={idx} className={`${styles.message} ${m.role === 'user' ? styles.user : styles.assistant}`}>
              <div className={styles.bubble}>
                {isSpeaking ? (
                  <SpeakingMessage
                    content={content}
                    styles={styles}
                    beepStatic={beepStatic}
                    BeepTalk={BeepTalk}
                  />
                ) : (
                  content
                )}
              </div>
            </div>
          );
        })}
        {isSending && (
          <div className={`${styles.message} ${styles.assistant}`}>
            <div className={styles.bubble} aria-live="polite" aria-busy="true">
              <LoadingIndicator />
            </div>
          </div>
        )}
      </div>
      {shouldTerminate ? (
        <div className={styles.inputRowFinish}>
          <Button
            type="button"
            variant="primary"
            size="md"
            className={styles.finishButton}
            ariaLabel="Finish interview"
            onClick={() => onShouldTerminate && onShouldTerminate(terminateConvId)}
          >
            Canvass Interview - Preview
          </Button>
        </div>
      ) : (
        <form className={styles.inputRow} onSubmit={onSend}>
          <textarea
            ref={textareaRef}
            className={styles.textarea}
            placeholder="Type your message..."
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              autoResize(e.target);
            }}
            onKeyDown={onTextareaKeyDown}
            rows={3}
          />
          <Button
            type="submit"
            variant="ghost"
            size="md"
            iconOnly
            ariaLabel="Send"
            className={styles.sendBtn}
            disabled={isSending || !input.trim()}
            iconRight={{ name: 'arrows/arrow_upward' }}
          />
        </form>
      )}
    </div>
  );
});

export default CanvassAssistantChat;
